import { trim } from 'lodash';
import { OverlayScrollbarsComponent as Scrollbars } from 'overlayscrollbars-react';
import { forwardRef, Ref, useImperativeHandle, useMemo, useRef } from 'react';
import { filterUnique } from 'lib/filter';
import { StyledScrollArea } from './ScrollArea.styled';
import { ScrollAreaProps } from './types';

export const ScrollArea = forwardRef((props: ScrollAreaProps, ref: Ref<HTMLElement>) => {
  const { className = '', flex = false, onScroll, ...rest } = props;

  // Reference to the internal OverlayScrollbarsComponent
  const innerRef = useRef<Scrollbars>(null);

  // Expose internal viewport as the target ref element
  useImperativeHandle(
    ref,
    () => innerRef.current?.osInstance()?.getElements().viewport as HTMLElement,
  );

  // Combine CSS class names that should be applied to the scroll area
  const combinedClassName = useMemo(() => {
    // Convert outer list of classnames into an array
    const sourceClasses = className
      .split(' ')
      .map(trim)
      .filter(name => !!name);

    // Merge custom classnames with externally specified ones
    const targetClasses = filterUnique(sourceClasses, ['os-theme-dark', 'os-theme-adelante']);

    // Add support for flexbox containers
    // @see https://kingsora.github.io/OverlayScrollbars/#!documentation/flexbox
    if (flex) {
      targetClasses.push('os-host-flexbox');
    }

    return targetClasses.join(' ');
  }, [className, flex]);

  return (
    <StyledScrollArea
      {...rest}
      className={combinedClassName}
      options={{
        callbacks: {
          onScroll,
        },
      }}
      ref={innerRef}
    />
  );
});

ScrollArea.displayName = 'ScrollArea';
